import type { ManualSection } from '../manual.types';

export const reportesSection: ManualSection = {
  slug: 'reportes',
  title: 'Reportes',
  icon: 'ki-document',
  summary:
    'Definición exacta de cada indicador, las cuatro pestañas, los filtros, la exportación y los límites de retención.',
  audience: 'QA y quien tenga que defender un número frente a un cliente.',
  topics: [
    {
      id: 'como-esta-armado',
      title: 'Cómo está armado el módulo',
      blocks: [
        {
          kind: 'p',
          text: 'Toda la pantalla se arma con un solo juego de filtros arriba. Al aplicarlos, cada pestaña pide sus propios datos: los indicadores de arriba salen del **resumen**, y cada pestaña de su endpoint. Cambiar de pestaña no vuelve a pedir el resumen.'
        },
        {
          kind: 'table',
          head: ['Pestaña', 'Qué muestra', 'Fuente de los datos'],
          rows: [
            [
              'Llamadas',
              'Una fila por llamada, paginada en el servidor. Al abrir una fila se ve la línea de tiempo de sus eventos.',
              'Detalle crudo (`cdr` / `call_events`)'
            ],
            [
              'Por día',
              'Un renglón por día y por operador (o por ruta).',
              'Tablas pre-agregadas diarias'
            ],
            [
              'Por hora',
              'Un renglón por hora y por operador (o por ruta), más la gráfica.',
              'Tablas pre-agregadas por hora'
            ],
            ['Causas de colgado', 'Conteo por `Hangup-Cause`, de mayor a menor.', 'Detalle crudo']
          ]
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Pre-agregado vs. crudo',
          text: 'Las pestañas por día y por hora leen tablas que se van sumando solas conforme llegan los eventos. Son rápidas pero solo guardan las columnas que alguien decidió guardar — por eso el bypass aparece por hora pero no por día. Las pestañas de Llamadas y Causas de colgado leen el detalle, que tiene todo pero es más pesado.'
        }
      ]
    },
    {
      id: 'definiciones',
      title: 'Qué significa exactamente cada indicador',
      blocks: [
        {
          kind: 'p',
          text: 'Estas definiciones aplican igual en el resumen, en el Histórico del dashboard y en las pestañas por día y por hora. Si vas a validar un número contra otra fuente, empieza por aquí.'
        },
        {
          kind: 'table',
          head: ['Indicador', 'Se cuenta como', 'Ojo con'],
          rows: [
            [
              '**Total**',
              'Eventos `channel_create` en el rango.',
              'Son **intentos**, no llamadas contestadas.'
            ],
            [
              '**Contestadas**',
              'Eventos `channel_answer`.',
              'Es el 200 OK del carrier. Un buzón que contesta también cuenta como contestada.'
            ],
            [
              '**Colgadas**',
              'Eventos `channel_hangup`.',
              'Puede no cuadrar con Total si el rango corta llamadas a la mitad.'
            ],
            [
              '**Humano**',
              'Eventos `cpd_result` con veredicto `human`.',
              'Solo llamadas donde el CPD corrió y concluyó.'
            ],
            [
              '**Buzón**',
              'Eventos `cpd_result` con veredicto `voicemail`.',
              'Igual que el anterior.'
            ],
            [
              '**Sin veredicto**',
              'Eventos `cpd_result` con veredicto `unknown`.',
              'El CPD corrió pero no alcanzó a decidir. **No** entra en los porcentajes.'
            ],
            [
              '**Bypass**',
              'Eventos `channel_hangup` marcados como bypass.',
              'El CPD no se aplicó. Ver la advertencia del resumen más abajo.'
            ],
            [
              '**Minutos facturables**',
              'Suma de `billsec` de los `channel_hangup`.',
              '`billsec` es tiempo **conectado**; una llamada que nunca contestó aporta 0.'
            ]
          ]
        },
        {
          kind: 'formula',
          label: 'Tasas de humano y buzón',
          expr: '% Humano = humano ÷ (humano + buzón)\n% Buzón  = buzón  ÷ (humano + buzón)',
          example:
            'Con 29 humano, 11 buzón y 8 sin veredicto: % Humano = 29 ÷ 40 = **72.5 %**. Las 8 sin veredicto quedan fuera del cálculo.',
          note: 'La API entrega estas tasas como fracción de 0 a 1; la pantalla las convierte a porcentaje. Si consumes la API directo, no te sorprenda ver `0.725`.'
        },
        {
          kind: 'note',
          tone: 'warn',
          title: 'El Bypass del resumen reporta 0 cuando no lo sabe',
          text: 'La tabla pre-agregada por día no tiene columna de bypass. El resumen solo trae el dato real cuando filtras **operador y ruta a la vez** (ese caso consulta el detalle). En cualquier otro caso muestra 0, que significa "no disponible", no "no hubo". Para el número real usa la pestaña **Por hora**, que sí lo guarda.'
        }
      ]
    },
    {
      id: 'zona-horaria',
      title: 'Zona horaria: los días y las horas son UTC',
      blocks: [
        {
          kind: 'p',
          text: 'La base analítica guarda cada evento con su marca de tiempo en **UTC** y las tablas por día y por hora agrupan usando esa marca. México está en **UTC−6**.'
        },
        {
          kind: 'table',
          head: ['Si eliges…', 'En realidad estás pidiendo (hora de México)'],
          rows: [
            ['Un día completo', 'De las **18:00 del día anterior** a las **17:59:59 de ese día**'],
            ['La hora "14:00" en la pestaña Por hora', 'Las **08:00** hora de México'],
            ['"Hoy" a media mañana', 'Desde las 18:00 de ayer hasta ahora']
          ]
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Consecuencia práctica para QA',
          text: 'El tráfico de una campaña que corre de 9:00 a 18:00 hora local aparece **repartido en un solo día UTC** (bien), pero la gráfica por hora se ve corrida 6 posiciones. Y una llamada de las 19:00 hora local cae en el **día siguiente** del reporte. Al comparar contra el Blaster o contra Voices monitor hay que hacer la conversión a mano.'
        },
        {
          kind: 'p',
          text: 'Es una diferencia conocida y documentada, no un dato perdido: el detalle de cada llamada trae su hora exacta. Dejarlo en hora local implica reconstruir las tablas pre-agregadas, así que se hará en una ventana de mantenimiento del backend.'
        }
      ]
    },
    {
      id: 'filtros',
      title: 'Los filtros',
      blocks: [
        {
          kind: 'kv',
          items: [
            { k: 'Rango de fechas', v: 'Obligatorio. Aplica a todas las pestañas.' },
            {
              k: 'Operador',
              v: 'El `X-Operator-Id` que mandó el Blaster (TELCEL, ATT, MOVISTAR, ALTAN…).'
            },
            {
              k: 'Grupo de ruta',
              v: 'El `routeGroupId` numérico (10 Telcel, 20 AT&T, 30 Movistar, 40 Altán, 99 onering).'
            },
            {
              k: 'Veredicto CPD',
              v: '`human`, `voicemail`, `unknown` o `bypass`. Solo aplica a la pestaña Llamadas.'
            },
            {
              k: 'Causa de colgado',
              v: 'Texto exacto del `Hangup-Cause`, por ejemplo `NORMAL_CLEARING` o `CALL_REJECTED`.'
            },
            { k: 'Contestadas', v: 'Sí / No. Filtra por si hubo o no `channel_answer`.' },
            { k: 'Bypass', v: 'Sí / No.' },
            {
              k: 'Duración facturable',
              v: 'Mínimo y máximo de `billsec` en segundos (0 a 86 400).'
            }
          ]
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Los filtros de veredicto y duración solo aplican a Llamadas',
          text: 'Las pestañas por día, por hora y de causas de colgado solo respetan rango, operador y grupo de ruta. Si filtras por veredicto y cambias a "Por día", ese filtro se ignora — no es un bug.'
        }
      ]
    },
    {
      id: 'detalle-llamada',
      title: 'Detalle de una llamada',
      blocks: [
        {
          kind: 'p',
          text: 'Al abrir una fila de la pestaña Llamadas se despliega la **línea de tiempo** de esa llamada: todos sus eventos en orden, con la hora de cada uno. Sirve para responder "¿por qué esta llamada se cortó?".'
        },
        {
          kind: 'kv',
          items: [
            {
              k: 'callUuid',
              v: 'El identificador único de la llamada en el FS. Es la llave para buscarla en cualquier otro lado.'
            },
            {
              k: 'Números A y B',
              v: 'Origen y destino. El destino trae el prefijo del Blaster (`PPPPP52NNNNNNNNNN`).'
            },
            {
              k: 'Veredicto y origen',
              v: 'Qué decidió el CPD y de dónde se dedujo: del evento `CPD::RESULT` o de las variables del colgado.'
            },
            {
              k: 'Tiempo de detección',
              v: 'Milisegundos desde que empezó el early media hasta que el CPD decidió.'
            },
            {
              k: 'sip_term_status',
              v: 'El código SIP con el que terminó. **603** es el que deja el CPD al cortar un buzón.'
            },
            { k: 'billsec / duration', v: 'Segundos conectados y segundos totales del canal.' }
          ]
        },
        {
          kind: 'code',
          label: 'Patrón que deja el CPD por tono al cortar un buzón',
          text: 'Hangup-Cause      = CALL_REJECTED   (causa 21)\nsip_term_status   = 603 Decline'
        }
      ]
    },
    {
      id: 'exportar',
      title: 'Exportar a CSV y Excel',
      blocks: [
        {
          kind: 'p',
          text: 'Los botones **CSV** y **Excel** de la pestaña Llamadas exportan con los **filtros aplicados en ese momento**, no solo la página que estás viendo. El archivo lo genera el backend y el navegador lo descarga.'
        },
        {
          kind: 'ul',
          items: [
            'La exportación no respeta la paginación: trae todo lo que cae dentro del filtro.',
            'Si el rango es muy amplio el archivo puede tardar; el botón queda en "Exportando…" mientras tanto.',
            'Si el backend rechaza los parámetros, hoy el mensaje de error sale genérico porque la respuesta viene como archivo y no como JSON. Si te pasa, achica el rango y vuelve a intentar.'
          ]
        }
      ]
    },
    {
      id: 'retencion',
      title: 'Cuánto tiempo hay datos (y por qué un reporte viejo sale vacío)',
      blocks: [
        {
          kind: 'table',
          head: ['Qué', 'Retención', 'Qué se ve afectado al vencer'],
          align: ['start', 'center', 'start'],
          rows: [
            [
              'Detalle de eventos y llamadas',
              '**90 días**',
              'Pestañas Llamadas y Causas de colgado, y el detalle de una llamada'
            ],
            ['Pre-agregado por día', '**365 días**', 'Pestaña Por día y el resumen'],
            ['Pre-agregado por hora', '**60 días**', 'Pestaña Por hora y su gráfica'],
            ['Auditoría', '**365 días**', 'Pantalla de Auditoría']
          ]
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Un reporte por hora de hace más de 60 días sale vacío y no es un bug',
          text: 'Es la retención más corta de todas. Si necesitas ese periodo, la pestaña **Por día** llega a un año.'
        }
      ]
    }
  ]
};
